import { useState, type ReactNode } from "react";
import { Check, PencilLine } from "lucide-react";
import { Button } from "@/components/form/Button";
import { cn } from "@/utils/helpers";
import { kitBareFieldClass } from "@/app/routes/dashboard/components/kit/Field";
import { useBrandIdentity } from "@/app/routes/dashboard/data/useCreativeOsHome";
import { formatLabel, latestReviewDecision, openDeclineFor, slotHasBrief, type CalendarMonthPlanWithSlots, type CalendarPlanSlot, type CalendarSlotIdea, type ConceptVersion, type ReviewIdeaSnapshot } from "@/types/contentCalendar.type";
import type { BriefRevisionEntry } from "@/types/productionBrief.type";
import { acceptedIdea } from "../slotStage";
import { useConceptRevision, useConceptVersions } from "../api/useConceptVersions";
import { useSlotIdeation } from "../api/useSlotIdeation";
import { useSlotBrief } from "../api/useProductionBrief";
import type { PostPhase } from "../components/PostHeader";
import PackageReviewWorkspace from "../review/PackageReviewWorkspace";
import ConceptReviewSurface from "./ConceptReviewSurface";
import { conceptDraftState, type ConceptReviewVersion } from "./conceptReviewModel";
import ConceptDocument, { type ConceptDocumentData } from "./ConceptDocument";
import ConceptDraftEditor from "./ConceptDraftEditor";
import ProductionBriefDoc from "../components/ProductionBriefDoc";
import { usePermissions } from "@/hooks/usePermissions";
import { useGuestReview } from "../guestCalendar";

function reviewVersion(version: ConceptVersion, slot: CalendarPlanSlot): ConceptReviewVersion {
  // A version whose stored snapshot could not be read still belongs in the
  // history — it was sent, and dropping it would leave a gap in the version
  // numbering. The slot supplies what the snapshot cannot.
  const snapshot = version.snapshot;
  if (!snapshot) {
    return {
      id: version.id, version: version.version, sharedAt: version.sharedAt,
      title: slot.title, synopsis: null, objectiveClaim: null, hooks: [],
      scheduledAt: slot.scheduledAt, pillarName: slot.pillarName,
      format: formatLabel(slot), reference: null,
      approved: version.decision?.decision === "APPROVED", decision: version.decision,
      discussion: version.comments.map(comment => ({ ...comment, version: version.version })),
    };
  }
  return {
    id: version.id, version: version.version, sharedAt: version.sharedAt,
    title: snapshot.title, synopsis: snapshot.synopsis, objectiveClaim: snapshot.objectiveClaim,
    hooks: snapshot.hooks, scheduledAt: snapshot.scheduledAt ?? slot.scheduledAt,
    pillarName: snapshot.pillarName ?? slot.pillarName,
    format: formatLabel({ ...slot, mediaType: snapshot.mediaType ?? slot.mediaType, aspectRatio: snapshot.aspectRatio, videoLength: snapshot.videoLength ?? slot.videoLength }),
    reference: snapshot.reference ?? null,
    approved: version.decision?.decision === "APPROVED", decision: version.decision,
    discussion: version.comments.map(comment => ({ ...comment, version: version.version })),
  };
}

/** The team's document from an idea on the board (the working draft, or the pick). */
function documentFromIdea(idea: CalendarSlotIdea, slot: CalendarPlanSlot): ConceptDocumentData {
  return {
    title: idea.title, synopsis: idea.synopsis, hooks: idea.hooks, objectiveClaim: idea.objectiveClaim,
    // A static has no running time.
    pillarName: slot.pillarName, durationSeconds: slot.mediaType === "STATIC" ? null : idea.durationSeconds, channels: idea.channels,
    reference: idea.reference, aspectRatio: idea.aspectRatio ?? slot.aspectRatio, sources: idea.sources,
    still: slot.mediaType === "STATIC",
  };
}

/** The team's document from a sent version's frozen snapshot. */
function documentFromSnapshot(snapshot: ReviewIdeaSnapshot, slot: CalendarPlanSlot): ConceptDocumentData {
  return {
    title: snapshot.title, synopsis: snapshot.synopsis, hooks: snapshot.hooks, objectiveClaim: snapshot.objectiveClaim,
    pillarName: snapshot.pillarName ?? slot.pillarName,
    durationSeconds: (snapshot.mediaType ?? slot.mediaType) === "STATIC" ? null : snapshot.durationSeconds,
    channels: snapshot.channels,
    reference: snapshot.reference ?? null, aspectRatio: snapshot.aspectRatio ?? slot.aspectRatio, sources: snapshot.sources ?? [],
    still: (snapshot.mediaType ?? slot.mediaType) === "STATIC",
  };
}

function sharedOn(value: string | null): string {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * The team's Concept tab after a pick: which version this is, the version
 * picker and the team's actions (edit, media, pull back, preview), then the
 * ConceptDocument, then the client's answer on that version.
 */
function TeamConceptDocument({ brandName, versions, selectedVersion, onVersionChange, toolbarActions, pillarHue, data, lead, onChangeReference }: {
  brandName: string; versions: ConceptReviewVersion[]; selectedVersion: number; onVersionChange: (version: number) => void;
  /** Null when there is nothing to do above the slide (Figma 08: the banner, then the slide). */
  toolbarActions: ReactNode | null; pillarHue?: string; data: ConceptDocumentData | null; lead?: ReactNode;
  /** The draft's Change media, on the slide's reference. */
  onChangeReference?: () => void;
}) {
  const ordered = [...versions].sort((a, b) => b.version - a.version);
  const current = ordered.find(version => version.version === selectedVersion) ?? ordered[0];
  if (!current) return null;
  const latestShared = ordered.find(version => version.sharedAt !== null) ?? null;
  const fallback: ConceptDocumentData = {
    title: current.title, synopsis: current.synopsis, hooks: current.hooks, objectiveClaim: current.objectiveClaim,
    pillarName: current.pillarName, durationSeconds: null, channels: [], reference: current.reference, aspectRatio: null,
  };
  const decision = current.decision;
  // An older sent version is read only: say so. The header's Version ▾ already names the rest.
  const readingOlder = current.sharedAt !== null && !!latestShared && latestShared.id !== current.id;
  return (
    <section aria-label={`Concept version ${current.version}`} className="flex flex-col gap-5">
      {toolbarActions || readingOlder ? <header className="flex flex-col gap-3 border-b border-co-line pb-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[13.5px] text-co-text2">
          {current.sharedAt === null ? `Version ${current.version} is the team's draft, not sent yet` : `Version ${current.version}, sent ${sharedOn(current.sharedAt)}`}
          {current.sharedAt !== null && latestShared && latestShared.id !== current.id ? ". Read only" : ""}
        </p>
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:justify-end">
          {ordered.length > 1 ? (
            <label className="flex items-center gap-2">
              <span className="text-[12.5px] text-co-muted">Version</span>
              <select
                value={current.version}
                onChange={event => onVersionChange(Number(event.target.value))}
                aria-label="Choose concept version"
                className={cn(kitBareFieldClass, "h-8 rounded-pill px-3 text-[13px]")}
              >
                {ordered.map(version => (
                  <option key={version.id} value={version.version}>
                    Version {version.version}{version.sharedAt ? ` · ${sharedOn(version.sharedAt)}` : " · draft"}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {toolbarActions}
        </div>
      </header> : null}
      {lead ? <div className="rounded-card bg-co-surface p-4">{lead}</div> : null}
      <ConceptDocument data={data ?? fallback} audience="team" brandName={brandName} pillarHue={pillarHue} onChangeReference={onChangeReference} />
      {decision ? (
        <div className="border-t border-co-line pt-4">
          <p className="text-[13.5px] text-co-text2">
            {decision.decision === "APPROVED" ? "Approved" : "Changes requested"} by {decision.clientName}, {sharedOn(decision.decidedAt)}
          </p>
          {decision.reason ? <blockquote dir="auto" className="mt-2 whitespace-pre-wrap border-l border-co-line2 pl-3 text-[14px] leading-relaxed text-co-text2">“{decision.reason}”</blockquote> : null}
        </div>
      ) : null}
    </section>
  );
}

function RevisionDirection({ guidance, busy, onSave, onRevise, onExplore }: {
  guidance: string; busy: boolean; onSave: (guidance: string) => Promise<unknown>;
  onRevise: (guidance: string) => Promise<unknown>; onExplore: (guidance: string) => Promise<unknown>;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [saved, setSaved] = useState(false);
  const value = draft ?? guidance;
  const act = async (action: (text: string) => Promise<unknown>) => {
    if (!value.trim() || busy) return;
    setError(false);
    try { await action(value.trim()); setSaved(true); } catch { setError(true); }
  };
  return (
    <section id="revision-direction" aria-label="Align on the revision" className="rounded-card bg-co-surface px-4 py-5 sm:px-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="tt-title-m font-semibold uppercase text-co-text">Revision direction</h3>
        <span className="text-[12.5px] text-co-muted">Team only</span>
      </div>
      <p className="tt-read-s mt-2 max-w-[75ch] text-co-muted">Read the client’s feedback above, then agree what stays and what changes. Nothing is regenerated until you choose to explore new directions.</p>
      <label className="mt-4 block">
        <span className="tt-read-s text-co-text2">What should we change?</span>
        <textarea id="revision-direction-input" rows={3} value={value} dir="auto" maxLength={2000} onChange={event => { setDraft(event.target.value); setSaved(false); }} placeholder="Keep the premise and reference. Make the opening clearer and bring the product in earlier…" className={cn(kitBareFieldClass, "mt-2 w-full resize-y px-3.5 py-3 text-base leading-relaxed sm:text-sm")} />
      </label>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <span role="status" className="tt-read-s text-co-muted">{error ? "Your direction is still here. Please try again." : saved ? "Direction saved for the team." : guidance && draft === null ? "Saved team direction" : "Save the direction before starting a revision."}</span>
        <div className="flex flex-wrap gap-2">
          <Button size="small" variant="secondary" buttonStyle="outline" isDisabled={busy || !value.trim()} onPress={() => void act(onSave)}>Save direction</Button>
          <Button size="small" icon={PencilLine} loading={busy} isDisabled={busy || !value.trim()} onPress={() => void act(onRevise)}>Revise this idea</Button>
        </div>
      </div>
      <details className="mt-4 border-t border-co-line pt-3">
        <summary className="tt-read-s cursor-pointer text-co-muted hover:text-co-text">Need a different idea instead?</summary>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <p className="tt-read-s max-w-[70ch] text-co-muted">Explore three new directions using this feedback. This replaces the working options; the version the client reviewed stays in history.</p>
          <Button size="small" variant="secondary" buttonStyle="outline" isDisabled={busy || !value.trim()} onPress={() => void act(onExplore)}>Explore new directions</Button>
        </div>
      </details>
    </section>
  );
}

/** The persistent item: chosen idea, shared versions, feedback and its separate brief. */
export default function ConceptItemWorkspace({ plan, slot, canEdit, phase, liveAllowed = false, onPhaseChange, onChangeMedia, pillarHue, editBriefRequest = 0, editIdeaRequest = 0, readOnlyRevision = null, onExitRevision, liveView, uploadView }: {
  plan: CalendarMonthPlanWithSlots; slot: CalendarPlanSlot; canEdit: boolean;
  phase: PostPhase; onPhaseChange: (phase: PostPhase) => void;
  /** The page's call on whether Live exists (approved, brief accepted), so both read one gate. */
  liveAllowed?: boolean;
  onPreview: () => void;
  onChangeMedia: (ideaId: string) => void;
  pillarHue?: string;
  /** Bumped by the banner's "Edit the brief": opens the brief editor. */
  editBriefRequest?: number;
  /** Bumped by the ⋯ menu's "Edit the idea": opens the idea's words on the working draft. */
  editIdeaRequest?: number;
  /** An older brief version picked from Version, read only. */
  readOnlyRevision?: BriefRevisionEntry | null;
  onExitRevision?: () => void;
  /** The Live tab, which the page owns so its banner can read the same stage. */
  liveView?: ReactNode;
  /** The Upload tab, which the page owns because it decides when the tab goes. */
  uploadView?: ReactNode;
  /** The team's Analytics tab, which reads the same stage as Live. */
}) {
  const brand = useBrandIdentity();
  // Only the Concept tab shows the shared versions; the brief and Live tabs never wait on them.
  const query = useConceptVersions(phase === "concept" ? slot.id : null);
  const revision = useConceptRevision(plan.id, slot.id);
  const ideation = useSlotIdeation(plan.id);
  // The brief exists from the pick on (it goes to the client with the concept).
  const briefQuery = useSlotBrief(slot.id, slotHasBrief(slot), plan.status, slot.status);
  const brief = briefQuery.data?.brief;
  // A Guest (the brand's client, logged in) reads this workspace and answers
  // as the client: approve or send back the shared concept, and comment.
  const { isGuest } = usePermissions();
  const guestReview = useGuestReview(plan.brandId, plan.id, slot.id);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [editing, setEditing] = useState<boolean | null>(null);
  const winner = acceptedIdea(slot);
  const draftState = conceptDraftState(slot, canEdit, !!query.data);
  // The team's unsent working draft is theirs: a guest sees what was shared.
  const { reopenedIdea, editable } = draftState;
  const draftIdea = isGuest ? null : draftState.idea;
  const visibleIdea = draftIdea ?? winner;
  const editorOpen = editing ?? !!reopenedIdea;
  const decline = openDeclineFor(slot);
  const latestDecision = latestReviewDecision(slot);
  // The brief is written from the pick and goes to the client with the concept (Figma 08).
  const briefStage = slotHasBrief(slot);
  const shared = (query.data?.versions ?? []).map(version => reviewVersion(version, slot));
  const nextVersion = Math.max(0, ...shared.map(version => version.version)) + 1;
  const draft: ConceptReviewVersion | null = draftIdea ? {
    id: `draft-${draftIdea.id}`, version: nextVersion, sharedAt: null,
    title: draftIdea.title, synopsis: draftIdea.synopsis, hooks: draftIdea.hooks,
    objectiveClaim: draftIdea.objectiveClaim, reference: draftIdea.reference,
    scheduledAt: slot.scheduledAt, pillarName: slot.pillarName, format: formatLabel({ ...slot, aspectRatio: draftIdea.aspectRatio }),
    approved: false, decision: null, discussion: [],
  } : null;
  const versions = draft ? [...shared, draft] : shared;
  const activeVersion = selectedVersion && versions.some(version => version.version === selectedVersion) ? selectedVersion : draft?.version ?? Math.max(0, ...shared.map(version => version.version));
  const activeShared = query.data?.versions?.find(version => version.version === activeVersion);
  const viewingDraft = !!draft && activeVersion === draft.version;
  const editingDraft = editorOpen && editable && viewingDraft && !!draftIdea;
  const busy = revision.saveAlignment.isPending || revision.reviseExisting.isPending || ideation.generateIdeas.isPending;
  const alignThen = async (guidance: string, action: "revise" | "explore") => {
    await revision.saveAlignment.mutateAsync(guidance);
    if (action === "revise") {
      await revision.reviseExisting.mutateAsync();
      setSelectedVersion(null); setEditing(true);
    } else {
      await ideation.generateIdeas.mutateAsync({ slotId: slot.id });
    }
  };

  // Above the slide only what moves the work on; Change media sits on the slide and
  // Edit the idea in the ⋯ menu (Figma 08).
  // Pull back lives in the ⋯ menu only.
  const backToDraft = editable && !viewingDraft;
  const chooseRevised = editable && viewingDraft && !!reopenedIdea;
  const toolbarActions = backToDraft || chooseRevised ? (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap gap-2">
        {backToDraft ? <Button size="small" variant="secondary" buttonStyle="outline" onPress={() => setSelectedVersion(draft?.version ?? null)}>Back to working draft</Button> : null}
      </div>
      <div className="flex flex-wrap gap-2">
        {chooseRevised && reopenedIdea ? <Button size="small" icon={Check} isDisabled={editorOpen || ideation.updateIdea.isPending} loading={ideation.acceptIdea.isPending} onPress={() => ideation.acceptIdea.mutate({ slotId: slot.id, ideaId: reopenedIdea.id })}>Choose revised idea</Button> : null}
      </div>
    </div>
  ) : null;
  const changeDraftMedia = editable && viewingDraft && draftIdea && !editorOpen ? () => onChangeMedia(draftIdea.id) : undefined;

  // The ⋯ menu's Edit the idea: back to the working draft, words open.
  const [seenEditIdea, setSeenEditIdea] = useState(editIdeaRequest);
  // A reopened idea is editable only once its history has loaded, which now
  // starts when the menu lands this on Concept: hold the request until then.
  const historyLoading = !!reopenedIdea && !query.data && !query.isError;
  if (editIdeaRequest !== seenEditIdea && !(editIdeaRequest > 0 && historyLoading)) {
    setSeenEditIdea(editIdeaRequest);
    if (editIdeaRequest > 0 && editable && draftIdea) {
      setSelectedVersion(null);
      setEditing(true);
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {phase === "upload" && liveAllowed ? (uploadView ?? null) : (phase === "publishing" || phase === "distribution") && liveAllowed ? (liveView ?? null) : phase === "brief" && briefStage && isGuest && !brief ? <p role="status" className="tt-read-m border-y border-co-line2 py-6 text-co-muted">The team is preparing the creative brief. It appears here when it is ready for your approval.</p> : phase === "brief" && briefStage && readOnlyRevision ? <section aria-label={`Creative brief, version ${readOnlyRevision.version}`} className="flex flex-col gap-4">
        <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-card bg-co-surface px-4 py-3">
          <p className="text-[13.5px] text-co-text2">Version {readOnlyRevision.version}, read only{brief ? `. The live brief is version ${brief.version}` : ""}</p>
          {onExitRevision ? <Button size="small" variant="secondary" buttonStyle="outline" onPress={onExitRevision}>Back to the live brief</Button> : null}
        </div>
        {/* Drawn like the live brief (unboxed, the post's frame and length), so two versions compare. */}
        <ProductionBriefDoc kind={brief?.kind ?? (slot.mediaType === "STATIC" ? "STATIC" : "VIDEO")} body={readOnlyRevision.body} showHeader={false} pillarHue={pillarHue} brandName={brand.name} askedSeconds={winner?.durationSeconds} aspectRatio={winner?.aspectRatio} />
      </section> : phase === "brief" && briefStage ? <PackageReviewWorkspace embedded plan={plan} slot={slot} canEdit={canEdit} onBack={() => onPhaseChange("concept")} pillarHue={pillarHue} editRequest={editBriefRequest} /> : <>
        {!versions.length || query.isError ? toolbarActions : null}

        {editingDraft && draftIdea ? <ConceptDraftEditor key={draftIdea.id} idea={draftIdea} saving={ideation.updateIdea.isPending} onCancel={() => setEditing(false)} onSave={fields => ideation.updateIdea.mutateAsync({ slotId: slot.id, ideaId: draftIdea.id, fields })} /> : null}

        {query.isLoading ? <p role="status" className="tt-read-s py-5 text-co-muted">Loading the shared versions and feedback…</p> : query.isError ? (
          <div role="alert" className="border-y border-co-line2 py-4">
            <p className="tt-read-s text-co-attention">Version history could not load. The current idea is shown below; retry before acting on feedback.</p>
            <Button size="small" variant="secondary" buttonStyle="outline" className="mt-3" onPress={() => void query.refetch()}>Retry history</Button>
          </div>
        ) : null}

        {editingDraft ? null : versions.length && !query.isError && isGuest ? <ConceptReviewSurface
          brandName={brand.name}
          versions={versions}
          selectedVersion={activeVersion}
          onVersionChange={setSelectedVersion}
          toolbarActions={toolbarActions}
          audience="client"
          canComment={slot.status === "IN_CLIENT_REVIEW"}
          canDecide={slot.status === "IN_CLIENT_REVIEW"}
          busy={guestReview.decideConcept.isPending || guestReview.commentOnConcept.isPending}
          onAddComment={body => { if (activeShared) guestReview.commentOnConcept.mutate({ versionId: activeShared.id, body }); }}
          onApprove={() => { if (activeShared) guestReview.decideConcept.mutate({ decision: "APPROVED", conceptVersionId: activeShared.id }); }}
          onRequestChanges={reason => { if (activeShared) guestReview.decideConcept.mutate({ decision: "DECLINED", reason, conceptVersionId: activeShared.id }); }}
        /> /* After the pick the team reads the idea as the ConceptDocument the client
           reads, with the version picker and the team's edit actions above it. */
        : versions.length && !query.isError ? <TeamConceptDocument brandName={brand.name} versions={versions} selectedVersion={activeVersion} onVersionChange={setSelectedVersion} toolbarActions={toolbarActions} onChangeReference={changeDraftMedia} pillarHue={pillarHue} data={viewingDraft && draftIdea ? documentFromIdea(draftIdea, slot) : activeShared?.snapshot ? documentFromSnapshot(activeShared.snapshot, slot) : null} lead={draft && latestDecision?.decision === "DECLINED" ? <div><p className="tt-read-s font-semibold text-co-text2">Responding to client feedback</p><blockquote className="tt-read-s mt-2 whitespace-pre-wrap text-co-muted">{latestDecision.reason}</blockquote><p className="tt-read-s mt-2 text-co-faint">Select the previous version to read its full discussion.</p></div> : undefined} /> : visibleIdea ? <ConceptDocument data={documentFromIdea(visibleIdea, slot)} audience="team" brandName={brand.name} pillarHue={pillarHue} onChangeReference={changeDraftMedia} /> : null}

        {decline && (!versions.length || query.isError) ? <div className="rounded-card bg-co-surface p-5"><p className="tt-read-s text-co-text2">{decline.clientName} asked for a change</p><blockquote className="tt-read-m mt-2 whitespace-pre-wrap text-co-muted">{decline.reason}</blockquote><p className="tt-read-s mt-4 text-co-text2">Reviewed idea: {decline.ideaSnapshot.title}</p><p className="tt-read-s mt-1 text-co-muted">{decline.ideaSnapshot.synopsis}</p></div> : null}

        {decline && canEdit && slot.status === "PLANNED" ? <RevisionDirection key={decline.id} guidance={query.data?.alignment?.guidance ?? ""} busy={busy || !query.isSuccess} onSave={guidance => revision.saveAlignment.mutateAsync(guidance)} onRevise={guidance => alignThen(guidance, "revise")} onExplore={guidance => alignThen(guidance, "explore")} /> : null}
        {draft && query.data?.alignment?.guidance ? <details className="border-y border-co-line py-3"><summary className="tt-read-s cursor-pointer text-co-muted">Agreed revision direction · team only</summary><p className="tt-read-m mt-3 whitespace-pre-wrap text-co-text2">{query.data.alignment.guidance}</p></details> : null}
      </>}
    </div>
  );
}
